import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../baseQuery';
import { apiClient } from '../baseQuery';

export const trainerApi = createApi({
	reducerPath: 'trainerApi',
	baseQuery: axiosBaseQuery,
	tagTypes: [
		'TrainerDashboard',
		'TrainerClasses',
		'ClassStudents',
		'StudentDetail',
		'TrainerCurricula',
		'TrainerCurriculumDetail',
		'SessionUnlocks',
		'CurriculumProgress',
	],
	endpoints: (builder) => ({
		getTrainerDashboard: builder.query<any, void>({
			query: () => ({ url: '/trainer/dashboard' }),
			providesTags: ['TrainerDashboard'],
		}),
		getTrainerClasses: builder.query<any, void>({
			query: () => ({ url: '/trainer/classes' }),
			providesTags: ['TrainerClasses'],
		}),
		getClassStudents: builder.query<any, string>({
			query: (classId) => ({ url: `/trainer/classes/${classId}/students` }),
			providesTags: (_result, _err, classId) => [
				{ type: 'ClassStudents', id: classId },
			],
		}),
		getStudentDetail: builder.query<any, string>({
			query: (studentId) => ({ url: `/trainer/students/${studentId}` }),
			providesTags: (_result, _err, studentId) => [
				{ type: 'StudentDetail', id: studentId },
			],
		}),
		getStudentAnalytics: builder.query<any, string>({
			query: (studentId) => ({
				url: `/trainer/students/${studentId}/analytics`,
			}),
		}),

		// ── Mutations ─
		createClass: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/trainer/classes',
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['TrainerDashboard', 'TrainerClasses'],
		}),
		deleteClass: builder.mutation<any, string>({
			query: (id) => ({ url: `/trainer/classes/${id}`, method: 'DELETE' }),
			invalidatesTags: ['TrainerDashboard', 'TrainerClasses'],
		}),
		addStudent: builder.mutation<
			any,
			{ classId: string; data: Record<string, unknown> }
		>({
			query: ({ classId, data }) => ({
				url: `/trainer/classes/${classId}/students`,
				method: 'POST',
				data,
			}),
			invalidatesTags: (_result, _err, { classId }) => [
				{ type: 'ClassStudents', id: classId },
				'TrainerDashboard',
			],
		}),
		removeStudent: builder.mutation<
			any,
			{ classId: string; studentId: string }
		>({
			query: ({ classId, studentId }) => ({
				url: `/trainer/classes/${classId}/students/${studentId}`,
				method: 'DELETE',
			}),
			invalidatesTags: (_result, _err, { classId }) => [
				{ type: 'ClassStudents', id: classId },
				'TrainerDashboard',
			],
		}),
		setCurriculum: builder.mutation<
			any,
			{ classId: string; curriculumId: string }
		>({
			query: ({ classId, curriculumId }) => ({
				url: `/trainer/classes/${classId}/curriculum`,
				method: 'PUT',
				data: { curriculumId },
			}),
			invalidatesTags: (_result, _err, { classId }) => [
				{ type: 'ClassStudents', id: classId },
			],
		}),
		resetStudentPin: builder.mutation<any, string>({
			query: (studentId) => ({
				url: `/trainer/students/${studentId}/reset-pin`,
				method: 'POST',
			}),
		}),
		enrollStudentFace: builder.mutation<
			{ success: boolean; faceSetAt: string },
			{ studentId: string; faceDescriptor: number[] }
		>({
			query: ({ studentId, faceDescriptor }) => ({
				url: `/trainer/students/${studentId}/enroll-face`,
				method: 'POST',
				data: { faceDescriptor },
			}),
			invalidatesTags: [{ type: 'ClassStudents' as const }],
		}),
		generateStudentPin: builder.mutation<any, string>({
			query: (studentId) => ({
				url: `/trainer/students/${studentId}/generate-pin`,
				method: 'POST',
			}),
		}),
		resetParentPassword: builder.mutation<any, string>({
			query: (studentId) => ({
				url: `/trainer/students/${studentId}/reset-parent-password`,
				method: 'POST',
			}),
		}),
		getParentCredentials: builder.query<
			{
				parentId: string;
				fullName: string;
				alforaaId: string;
				email: string;
				mustChangePassword: boolean;
			},
			string
		>({
			query: (studentId) => ({
				url: `/trainer/students/${studentId}/parent-credentials`,
				method: 'GET',
			}),
		}),
		transferStudentSection: builder.mutation<
			any,
			{ studentId: string; newClassId: string }
		>({
			query: ({ studentId, newClassId }) => ({
				url: `/trainer/students/${studentId}/transfer-section`,
				method: 'PATCH',
				data: { newClassId },
			}),
			invalidatesTags: ['TrainerClasses', 'TrainerDashboard'],
		}),
		approveStudent: builder.mutation<any, Record<string, unknown>>({
			query: (body) => ({
				url: '/trainer/students/approve',
				method: 'POST',
				data: body,
			}),
		}),

		// ── Curriculum ─
		getTrainerCurricula: builder.query<any, void>({
			query: () => ({ url: '/trainer/curricula' }),
			providesTags: ['TrainerCurricula'],
		}),
		getTrainerCurriculumDetail: builder.query<any, string>({
			query: (id) => ({ url: `/trainer/curricula/${id}` }),
			providesTags: (_result, _err, id) => [
				{ type: 'TrainerCurriculumDetail', id },
			],
		}),
		setClassCurricula: builder.mutation<
			any,
			{ classId: string; curriculumIds: string[] }
		>({
			query: ({ classId, curriculumIds }) => ({
				url: `/trainer/classes/${classId}/curricula`,
				method: 'PUT',
				data: { curriculumIds },
			}),
			invalidatesTags: ['TrainerDashboard', 'TrainerClasses'],
		}),

		// ── Unlock ─
		unlockSession: builder.mutation<
			any,
			{
				classId: string;
				curriculumId: string;
				moduleIndex: number;
				sessionIndex: number;
			}
		>({
			query: ({ classId, curriculumId, moduleIndex, sessionIndex }) => ({
				url: `/trainer/classes/${classId}/curriculum/${curriculumId}/unlock-session`,
				method: 'POST',
				data: { moduleIndex, sessionIndex },
			}),
			invalidatesTags: ['SessionUnlocks'],
		}),
		unlockModule: builder.mutation<
			any,
			{ classId: string; curriculumId: string; moduleIndex: number }
		>({
			query: ({ classId, curriculumId, moduleIndex }) => ({
				url: `/trainer/classes/${classId}/curriculum/${curriculumId}/unlock-module`,
				method: 'POST',
				data: { moduleIndex },
			}),
			invalidatesTags: ['SessionUnlocks'],
		}),
		getUnlocks: builder.query<any, { classId: string; curriculumId: string }>({
			query: ({ classId, curriculumId }) => ({
				url: `/trainer/classes/${classId}/curriculum/${curriculumId}/unlocks`,
			}),
			providesTags: ['SessionUnlocks'],
		}),
		setSessionStatus: builder.mutation<
			any,
			{
				classId: string;
				curriculumId: string;
				moduleIndex: number;
				sessionIndex: number;
				status: string;
				notes?: string;
			}
		>({
			query: ({
				classId,
				curriculumId,
				moduleIndex,
				sessionIndex,
				status,
				notes,
			}) => ({
				url: `/trainer/classes/${classId}/curriculum/${curriculumId}/sessions/${moduleIndex}/${sessionIndex}/status`,
				method: 'PUT',
				data: { status, notes },
			}),
			invalidatesTags: ['SessionUnlocks', 'CurriculumProgress'],
		}),
		getCurriculumProgress: builder.query<
			any,
			{ classId: string; curriculumId: string }
		>({
			query: ({ classId, curriculumId }) => ({
				url: `/trainer/classes/${classId}/curriculum/${curriculumId}/progress`,
			}),
			providesTags: ['SessionUnlocks', 'CurriculumProgress'],
		}),
		getStudentSessionReview: builder.query<
			any,
			{ studentId: string; sessionId: string }
		>({
			query: ({ studentId, sessionId }) => ({
				url: `/trainer/students/${studentId}/sessions/${sessionId}/review`,
			}),
		}),
		getStudentCurriculumProgress: builder.query<any, string>({
			query: (studentId) => ({
				url: `/trainer/students/${studentId}/curriculum-progress`,
			}),
		}),
		uploadRoster: builder.mutation<any, { classId: string; file: File }>({
			queryFn: async ({ classId, file }) => {
				try {
					const formData = new FormData();
					formData.append('file', file);
					const result = await apiClient.post(
						`/trainer/classes/${classId}/students/upload`,
						formData,
						{ headers: { 'Content-Type': 'multipart/form-data' } },
					);
					return { data: result.data };
				} catch (err: any) {
					return {
						error: {
							status: err.response?.status,
							data: err.response?.data,
							message: err.message,
						},
					};
				}
			},
			invalidatesTags: (_result, _err, { classId }) => [
				{ type: 'ClassStudents', id: classId },
				'TrainerDashboard',
			],
		}),
	}),
});

export const {
	useGetTrainerDashboardQuery,
	useGetTrainerClassesQuery,
	useGetClassStudentsQuery,
	useGetStudentDetailQuery,
	useGetStudentAnalyticsQuery,
	useCreateClassMutation,
	useDeleteClassMutation,
	useAddStudentMutation,
	useRemoveStudentMutation,
	useSetCurriculumMutation,
	useResetStudentPinMutation,
	useEnrollStudentFaceMutation,
	useGenerateStudentPinMutation,
	useResetParentPasswordMutation,
	useGetParentCredentialsQuery,
	useLazyGetParentCredentialsQuery,
	useApproveStudentMutation,
	useUploadRosterMutation,
	useGetTrainerCurriculaQuery,
	useGetTrainerCurriculumDetailQuery,
	useSetClassCurriculaMutation,
	useUnlockSessionMutation,
	useUnlockModuleMutation,
	useGetUnlocksQuery,
	useSetSessionStatusMutation,
	useGetCurriculumProgressQuery,
	useGetStudentSessionReviewQuery,
	useGetStudentCurriculumProgressQuery,
	useTransferStudentSectionMutation,
} = trainerApi;
